import { InviteeStatusEnum } from '../../models/jobInviteeStatusEnum';
import { RoleEnum } from '../../models/role.enum';
import { ShiftEnum } from '../../models/shift.enum';
import { TranslationKeyTranslation } from '../../models/translation';
import { User } from '../../models/user';
import { getInviteesMongoSearchQuery } from '../getUsersMongoSearchQuery';
import { MongoSearchQueryParams } from './JobsMongoQueryOptions';
import { SearchTypes } from './SearchTypes';

/**
 * Helper function to get matching shift enums for a search text based on i18n translations
 */
export function getMatchingShiftsForSearchText(
  text: string,
  i18n?: TranslationKeyTranslation,
): number[] {
  if (!i18n?.shifts || !text) {
    return [];
  }

  return Object.keys(ShiftEnum)
    .filter((shiftEnum) => {
      return !!i18n.shifts[shiftEnum]
        ?.toUpperCase()
        .includes(text.toUpperCase());
    })
    .map((shiftEnum) => Number.parseInt(shiftEnum, 10));
}

export function getJobSearchMongoQuery(
  queryParams: MongoSearchQueryParams,
  user: User,
  activeSearchType: SearchTypes = 'ALL',
  matchingShifts: number[] = [],
): object[] {
  if (!queryParams[activeSearchType] || queryParams[activeSearchType] == '') {
    return [];
  }

  let searchText = queryParams[activeSearchType] || '';
  searchText = searchText.replace('+', '\\+');
  if (searchText.length === 0) {
    return [];
  }

  return [
    {
      $or: getQueryForSearch(
        searchText,
        user,
        activeSearchType,
        matchingShifts,
      ),
    },
  ];
}

function getPrefixRegexSearchCondition(searchText: string) {
  return {
    $regex: `^${searchText}`,
  };
}

function getQueryForSearch(
  searchText: string,
  user: User,
  activeSearchType: SearchTypes = 'ALL',
  matchingShifts: number[] = [],
): object[] {
  searchText = searchText.replace(/\(/g, '\\(');
  searchText = searchText.replace(/\[/g, '\\[');
  searchText = searchText.replace(/\)/g, '\\)');

  let searchQuery: object[] = [];
  switch (activeSearchType) {
    case 'ALL': {
      if (user.role === RoleEnum.DISPO && user.customerId) {
        searchQuery = getInviteesMongoSearchQuery(searchText, user.customerId);
      }
      searchQuery.push(
        ...[
          {
            orderId: getPrefixRegexSearchCondition(searchText),
          },
          {
            'department.costCenter': getPrefixRegexSearchCondition(searchText),
          },
          {
            caseNumber: getPrefixRegexSearchCondition(searchText),
          },
          {
            _id: searchText,
          },
          {
            'shifts.label': getPrefixRegexSearchCondition(searchText),
          },
          {
            'shifts.label': { $exists: false },
            'shifts.shift': { $in: matchingShifts },
          },
        ],
      );
      break;
    }
    case 'USER': {
      if (user.role === RoleEnum.DISPO && user.customerId) {
        searchQuery = getInviteesMongoSearchQuery(searchText, user.customerId);
      }
      break;
    }
    case 'DESCRIPTION':
      searchQuery = [
        {
          description: getPrefixRegexSearchCondition(searchText),
        },
        {
          caseNumber: getPrefixRegexSearchCondition(searchText),
        },
      ];
      break;
    case 'BOOKED_USER':
      if (user.role === RoleEnum.DISPO && user.customerId) {
        searchQuery = getInviteesMongoSearchQuery(
          searchText,
          user.customerId,
          InviteeStatusEnum.BOOKED,
        );
      }
      break;
    case 'CANCELED_USER':
      if (user.role === RoleEnum.DISPO && user.customerId) {
        searchQuery = getInviteesMongoSearchQuery(
          searchText,
          user.customerId,
          InviteeStatusEnum.CANCELED,
        );
      }
      break;
    case 'SHIFT':
      searchQuery = [
        {
          'shifts.label': getPrefixRegexSearchCondition(searchText),
        },
        {
          'shifts.label': { $exists: false },
          'shifts.shift': { $in: matchingShifts },
        },
      ];
      break;
  }
  return searchQuery;
}
